/**
 * The in-app explainer shown before ONE asks the browser for
 * notification permission.
 *
 * Deliberately a dismissible BANNER, not a blocking modal: ONE is a
 * guardian's calm-state app most of the time, and a modal on every login for
 * an optional setting is the pattern that trains people to dismiss without
 * reading — the opposite of what a life-safety notification needs. The banner
 * states what ONE actually sends (reunification pickup, student check-in) so
 * the native prompt that follows is an informed yes.
 */
import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

export interface PushPrimingBannerProps {
  /** Accept → the caller lets registration run, surfacing the native prompt. */
  onEnable: () => void;
  /** "Not now" → the caller remembers the dismissal for the cooldown. */
  onDismiss: () => void;
}

export function PushPrimingBanner({
  onEnable,
  onDismiss,
}: PushPrimingBannerProps) {
  const { t } = useTranslation();

  return (
    <Box
      role="region"
      aria-label={t('pushPriming.title')}
      bg="bg.subtle"
      borderBottomWidth="1px"
      borderColor="border.line"
      px={4}
      py={3}
    >
      <VStack align="stretch" gap={2}>
        <Text fontWeight="600">{t('pushPriming.title')}</Text>
        <Text fontSize="sm" color="text.secondary">
          {t('pushPriming.body')}
        </Text>
        <HStack gap={2}>
          <Button size="sm" onClick={onEnable}>
            {t('pushPriming.enable')}
          </Button>
          <Button size="sm" variant="ghost" onClick={onDismiss}>
            {t('pushPriming.notNow')}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
